import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { Municipality, Nucleo, Region } from "./types";

/**
 * Generates a PDF report listing all cities grouped by region.
 * Each region gets its own section with a colored header showing the
 * region name, responsible person, and city count, plus the region's
 * núcleos (ABCCC) with their key contacts.
 */
export function generateRegionReport(regions: Region[], municipalities: Municipality[], nucleos: Nucleo[] = []) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  // ── Title page ──
  drawTitlePage(doc, pageWidth, pageHeight, regions, municipalities);

  // ── Region sections ──
  const sortedRegions = [...regions].sort((a, b) => a.sort_order - b.sort_order);

  for (const region of sortedRegions) {
    const cities = municipalities
      .filter((m) => m.region_id === region.id)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));

    const regionNucleos = nucleos
      .filter((n) => n.region_id === region.id)
      .sort((a, b) => a.cidade.localeCompare(b.cidade, "pt-BR") || a.nome.localeCompare(b.nome, "pt-BR"));

    if (cities.length === 0 && regionNucleos.length === 0) continue;

    doc.addPage();

    let y = margin;

    // Region header bar
    const headerColor = hexToRgb(region.color_hex) || { r: 100, g: 100, b: 100 };
    doc.setFillColor(headerColor.r, headerColor.g, headerColor.b);
    doc.roundedRect(margin, y, contentWidth, 18, 3, 3, "F");

    // Region name (white text on colored bar)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(region.name, margin + 6, y + 8);

    // Responsible + counts
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      `${region.current_responsible_name}  •  ${cities.length} ${cities.length === 1 ? "cidade" : "cidades"}` +
        `  •  ${regionNucleos.length} ${regionNucleos.length === 1 ? "núcleo" : "núcleos"}`,
      margin + 6,
      y + 14,
    );

    y += 24;

    // ── Núcleos da região ──
    if (regionNucleos.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text(`Núcleos (${regionNucleos.length})`, margin, y + 4);
      y += 7;

      const nucleoBody = regionNucleos.map((n, idx) => [
        String(idx + 1),
        n.nome,
        `${n.cidade}/${n.uf}`,
        n.presidente_nome || "—",
        n.presidente_celular || n.telefone || "—",
        n.mandato || "—",
      ]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["#", "Núcleo", "Cidade/UF", "Presidente", "Contato", "Mandato"]],
        body: nucleoBody,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          lineColor: [220, 220, 220],
          lineWidth: 0.2,
          textColor: [40, 40, 40],
        },
        headStyles: {
          fillColor: [headerColor.r, headerColor.g, headerColor.b],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        columnStyles: {
          0: { cellWidth: 8, halign: "center" },
          2: { cellWidth: 26 },
          4: { cellWidth: 26 },
          5: { cellWidth: 18, halign: "center" },
        },
        didDrawPage: () => {
          drawFooter(doc, pageWidth, pageHeight);
        },
      });

      y = (doc as any).lastAutoTable.finalY + 8;
      if (y > pageHeight - 30) {
        doc.addPage();
        y = margin;
      }
    }

    if (cities.length === 0) continue;

    // Cidades label (separa da seção de núcleos)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Cidades (${cities.length})`, margin, y + 4);
    y += 7;

    // Group cities by state
    const byState = new Map<string, Municipality[]>();
    for (const city of cities) {
      const list = byState.get(city.uf) || [];
      list.push(city);
      byState.set(city.uf, list);
    }
    const stateEntries = Array.from(byState.entries()).sort(([a], [b]) => a.localeCompare(b, "pt-BR"));

    for (const [uf, stateCities] of stateEntries) {
      // State sub-header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(`${uf} — ${stateCities[0]?.state_name ?? ""} (${stateCities.length})`, margin, y + 4);
      y += 7;

      // Cities table using autoTable
      const tableBody = stateCities.map((city, idx) => [
        String(idx + 1),
        city.name,
      ]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["#", "Cidade"]],
        body: tableBody,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          lineColor: [220, 220, 220],
          lineWidth: 0.2,
          textColor: [40, 40, 40],
        },
        headStyles: {
          fillColor: [headerColor.r, headerColor.g, headerColor.b],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: "auto" },
        },
        didDrawPage: () => {
          drawFooter(doc, pageWidth, pageHeight);
        },
      });

      // Update y position after table
      y = (doc as any).lastAutoTable.finalY + 8;

      // Check if we need a new page for next state section
      if (y > pageHeight - 30) {
        doc.addPage();
        y = margin;
      }
    }
  }

  // ── Summary page ──
  doc.addPage();
  drawSummaryPage(doc, pageWidth, pageHeight, margin, contentWidth, sortedRegions, municipalities, nucleos);

  // Add footer to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, pageWidth, pageHeight);
  }

  // Save
  const fileName = buildFileName(sortedRegions);
  doc.save(`${fileName}.pdf`);
}

function buildFileName(regions: Region[]): string {
  return `${buildRegionLabel(regions)} - ${fileTimestamp()}`;
}

function buildRegionLabel(regions: Region[]): string {
  if (regions.length === 0) {
    return "Relatório de Regiões";
  }
  if (regions.length === 1) {
    return regions[0].name;
  }
  const identifiers = regions.map((r) =>
    r.name.startsWith("Região ") ? r.name.substring("Região ".length) : r.name,
  );
  if (identifiers.length === 2) {
    return `Regiões ${identifiers[0]} e ${identifiers[1]}`;
  }
  const last = identifiers[identifiers.length - 1];
  const rest = identifiers.slice(0, -1).join(", ");
  return `Regiões ${rest} e ${last}`;
}

// "DD-MM-YY HHhMM" — formato BR sem os caracteres / e : (proibidos em nomes de
// arquivo no Windows, que o navegador trocaria por _).
function fileTimestamp(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${String(d.getFullYear()).slice(-2)} ${p(d.getHours())}h${p(d.getMinutes())}`;
}

function drawTitlePage(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  regions: Region[],
  municipalities: Municipality[],
) {
  // Background gradient simulation
  doc.setFillColor(12, 21, 36);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  // Decorative accent bar at top
  doc.setFillColor(180, 101, 66);
  doc.rect(0, 0, pageWidth, 3, "F");

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(241, 245, 249);
  doc.text("Sul Remates", pageWidth / 2, pageHeight * 0.32, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(16);
  doc.setTextColor(148, 163, 184);
  doc.text("Relatório de Regiões Comerciais", pageWidth / 2, pageHeight * 0.38, { align: "center" });

  // Stats
  const statsY = pageHeight * 0.50;
  doc.setFillColor(18, 32, 58);
  doc.roundedRect(pageWidth / 2 - 55, statsY - 10, 110, 36, 4, 4, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(241, 245, 249);
  doc.text(String(municipalities.length), pageWidth / 2 - 30, statsY + 6, { align: "center" });
  doc.text(String(regions.length), pageWidth / 2 + 30, statsY + 6, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text(municipalities.length === 1 ? "cidade" : "cidades", pageWidth / 2 - 30, statsY + 14, { align: "center" });
  doc.text(regions.length === 1 ? "região" : "regiões", pageWidth / 2 + 30, statsY + 14, { align: "center" });

  // Date
  const now = new Date();
  const dateFormatted = now.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(`Gerado em ${dateFormatted}`, pageWidth / 2, pageHeight * 0.65, { align: "center" });

  // Region list preview
  let previewY = pageHeight * 0.72;
  doc.setFontSize(9);
  for (const r of [...regions].sort((a, b) => a.sort_order - b.sort_order)) {
    const color = hexToRgb(r.color_hex) || { r: 100, g: 100, b: 100 };
    doc.setFillColor(color.r, color.g, color.b);
    doc.circle(pageWidth / 2 - 40, previewY - 1.5, 2.5, "F");

    doc.setTextColor(200, 200, 210);
    doc.text(`${r.name} — ${r.current_responsible_name}`, pageWidth / 2 - 34, previewY);
    previewY += 6;
  }
}

function drawSummaryPage(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  margin: number,
  contentWidth: number,
  regions: Region[],
  municipalities: Municipality[],
  nucleos: Nucleo[],
) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text("Resumo por Região", margin, margin + 8);

  const summaryBody = regions.map((r) => {
    const count = municipalities.filter((m) => m.region_id === r.id).length;
    const nucCount = nucleos.filter((n) => n.region_id === r.id).length;
    const states = new Set(municipalities.filter((m) => m.region_id === r.id).map((m) => m.uf));
    return [r.name, r.current_responsible_name, String(count), String(nucCount), Array.from(states).sort().join(", ")];
  });

  autoTable(doc, {
    startY: margin + 14,
    margin: { left: margin, right: margin },
    head: [["Região", "Responsável", "Cidades", "Núcleos", "Estados"]],
    body: summaryBody,
    theme: "striped",
    styles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: [40, 40, 40],
    },
    headStyles: {
      fillColor: [180, 101, 66],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      2: { halign: "center", cellWidth: 18 },
      3: { halign: "center", cellWidth: 18 },
    },
  });

  // Total
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Total: ${municipalities.length} cidades e ${nucleos.length} núcleos em ${regions.length} regiões`,
    margin,
    finalY,
  );

  drawFooter(doc, pageWidth, pageHeight);
}

function drawFooter(doc: jsPDF, pageWidth: number, pageHeight: number) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text("Sul Remates — Regiões Comerciais", 14, pageHeight - 8);
  doc.text(
    `Página ${doc.getCurrentPageInfo().pageNumber}`,
    pageWidth - 14,
    pageHeight - 8,
    { align: "right" },
  );
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}
